import {cn} from "@/flokit/utils/styling/cn";
import {cx} from "class-variance-authority";
import {forwardRef, useState} from "react";
import {VStack} from "../composition/stack.layout";
import {useHtmlStringToTextContent} from "../utils/useHtmlStringToTextContent";
import WrapClampSpan from "../wrap-clamp-span";
import {RichTextEditorBase} from "./rich-text-editor-base";

export type ExpandableViewCutoffParams = {
  maxChars?: number;
  wrappingThreshold?: number;
};

interface ExpandableReadonlyRichTextViewProps {
  content: string;
  defaultContent?: string;
  cutoffParams: ExpandableViewCutoffParams;
  textClassName?: string;
  tabIndex?: number;
  className?: string;
}
export const ExpandableReadonlyRichTextView = forwardRef<
  HTMLButtonElement,
  ExpandableReadonlyRichTextViewProps
>((props, ref) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const textContent = useHtmlStringToTextContent(
    props.content,
    props.defaultContent,
  );

  const textTooLong =
    (textContent?.length ?? 0) > (props.cutoffParams.maxChars ?? 0);
  const shouldTruncateText = textTooLong && !isExpanded;

  const buttonClasses = cx(
    "font-inter font-semibold text-navy-sailor text-base",
    "focus:outline-offset-2",
  );

  const textClasses =
    props.textClassName ??
    "font-inter text-charcoal text-base outline-offset-4";

  return (
    <>
      {/* Truncated view */}
      <p
        className={cn(
          textClasses,
          props.className,
          shouldTruncateText ? "" : "hidden",
        )}
        tabIndex={-1}
      >
        <WrapClampSpan wrappingThreshold={props.cutoffParams.wrappingThreshold}>
          {textContent?.slice(0, props.cutoffParams.maxChars) + "..."}
        </WrapClampSpan>
        <button
          ref={ref}
          tabIndex={props.tabIndex}
          className={cx(buttonClasses)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsExpanded(true);
          }}
        >
          View all
        </button>
      </p>
      {/* Expanded view */}
      <VStack
        className={cn(
          "gap-y-2 outline-offset-4",
          props.className,
          // We use this method to show/hide the expanded view, rather than conditional rendering, so that the rich text editor (RTE) is always mounted. When the RTE mounts, there is a brief initialization frame that would be noticeable on toggle. We avoid this initialization frame by always rendering the RTE.
          shouldTruncateText ? "hidden" : "",
        )}
        // Programatically focus on expansion
        tabIndex={-1}
      >
        <RichTextEditorBase
          editorClassName="no-padding"
          content={
            textContent
              ? textContent.length > 0
                ? props.content
                : props.defaultContent ?? ""
              : ""
          }
          textClassName={textClasses}
          readOnly
        />
        {textTooLong && (
          <button
            ref={ref}
            className={cx(buttonClasses, "self-end")}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsExpanded(false);
            }}
          >
            View less
          </button>
        )}
      </VStack>
    </>
  );
});
